import { Check, X, Printer, Award, RotateCcw, FileDown } from "lucide-react";
import { CATEGORIES, ZVANJA } from "../data/pravilnik";
import { exportDocx } from "../lib/docxExport";

const CheckRow = ({ check }) => (
  <div className="flex items-start gap-3 py-2" data-testid={`check-${check.id}`}>
    <span
      className={`mt-0.5 w-5 h-5 rounded-full flex items-center justify-center shrink-0 ${
        check.ok ? "bg-emerald-100 text-emerald-600" : "bg-red-100 text-red-500"
      }`}
    >
      {check.ok ? <Check size={13} strokeWidth={3} /> : <X size={13} strokeWidth={3} />}
    </span>
    <div className="flex-1 min-w-0">
      <p className="text-sm text-slate-700 leading-snug">{check.label}</p>
      {!check.ok && check.missing && (
        <p className="text-xs text-red-500 mt-0.5">{check.missing}</p>
      )}
    </div>
    <span className={`text-xs font-semibold tabular-nums shrink-0 ${check.ok ? "text-emerald-600" : "text-slate-400"}`}>
      {check.value}
    </span>
  </div>
);

export const Results = ({ target, basic, counts, calc, result, onReset }) => {
  const zvanje = ZVANJA[target];
  const pct = Math.min(100, Math.round((calc.total / result.neededPoints) * 100));

  return (
    <section className="bg-white p-5 sm:p-6 rounded-xl shadow-sm border border-slate-200 space-y-5" data-testid="results-panel">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-slate-800 font-heading">Rezultat</h2>
        <button
          type="button"
          onClick={onReset}
          data-testid="reset-btn"
          className="inline-flex items-center gap-1.5 text-xs font-medium text-slate-500 hover:text-slate-700 transition-colors duration-200"
        >
          <RotateCcw size={13} /> Poništi unos
        </button>
      </div>

      <div
        className={`rounded-xl p-4 border ${
          result.eligible
            ? "bg-emerald-50 border-emerald-200"
            : "bg-slate-50 border-slate-200"
        }`}
        data-testid="eligibility-banner"
      >
        <div className="flex items-center gap-3">
          <Award size={28} className={result.eligible ? "text-emerald-600" : "text-slate-400"} />
          <div>
            <p className={`text-sm font-bold ${result.eligible ? "text-emerald-700" : "text-slate-700"}`}>
              {result.eligible
                ? `Ispunjavate uvjete za zvanje: ${zvanje.label}`
                : `Uvjeti za zvanje ${zvanje.label} još nisu ispunjeni`}
            </p>
            <p className="text-xs text-slate-500 mt-0.5">
              {result.eligible
                ? "Prema unesenim podacima ispunjeni su svi provjereni uvjeti Pravilnika."
                : "Pogledajte popis u nastavku. Crveno označava što nedostaje."}
            </p>
          </div>
        </div>
      </div>

      <div>
        <div className="flex justify-between items-baseline mb-1.5">
          <span className="text-xs font-bold uppercase tracking-wider text-slate-500">Ukupno bodova</span>
          <span className="text-2xl font-bold text-slate-900 tabular-nums" data-testid="total-points">
            {calc.total}
            <span className="text-sm font-medium text-slate-400"> / {result.neededPoints}</span>
          </span>
        </div>
        <div className="w-full bg-slate-100 rounded-full h-3 overflow-hidden">
          <div
            className={`h-full rounded-full transition-all duration-500 ${calc.total >= result.neededPoints ? "bg-emerald-500" : "bg-blue-600"}`}
            style={{ width: `${pct}%` }}
            data-testid="points-progress-bar"
          />
        </div>
        {result.useAlt && (
          <p className="text-xs text-blue-600 mt-2 bg-blue-50 border border-blue-100 rounded-lg p-2">
            Primjenjuje se iznimka za mentora (3–5 god. staža): potrebno je 40 bodova iz najmanje 4 kategorije.
          </p>
        )}
      </div>

      <div>
        <h3 className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">Bodovi po kategorijama</h3>
        <div className="space-y-1.5">
          {CATEGORIES.map((cat) => {
            const pts = calc.byCategory[cat.id] || 0;
            const w = calc.total > 0 ? Math.max(pts > 0 ? 4 : 0, Math.round((pts / Math.max(calc.total, result.neededPoints)) * 100)) : 0;
            return (
              <div key={cat.id} className="flex items-center gap-2" data-testid={`result-category-${cat.id}`}>
                <span className="text-xs text-slate-600 w-44 sm:w-52 truncate shrink-0" title={cat.title}>
                  {cat.title}
                </span>
                <div className="flex-1 bg-slate-100 rounded-full h-2 overflow-hidden">
                  <div className="h-full bg-blue-500 rounded-full transition-all duration-500" style={{ width: `${w}%` }} />
                </div>
                <span className={`text-xs font-semibold tabular-nums w-8 text-right ${pts > 0 ? "text-slate-700" : "text-slate-300"}`}>
                  {pts}
                </span>
              </div>
            );
          })}
        </div>
      </div>

      <div>
        <h3 className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1">Provjera uvjeta</h3>
        <div className="divide-y divide-slate-100">
          {result.checks.map((c) => (
            <CheckRow key={c.id} check={c} />
          ))}
        </div>
      </div>

      <div className="space-y-2">
        <button
          type="button"
          onClick={() => exportDocx({ basic, target, counts, calc })}
          disabled={calc.total === 0}
          data-testid="docx-btn"
          className="w-full inline-flex items-center justify-center gap-2 rounded-lg bg-blue-600 px-4 py-2.5 text-sm font-medium text-white hover:bg-blue-700 transition-colors duration-200 focus-visible:ring-2 focus-visible:ring-blue-600 focus-visible:outline-none disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <FileDown size={16} /> Preuzmi tablicu za Agenciju (.docx)
        </button>
        <p className="text-xs text-slate-400 leading-snug">
          Word tablica s unesenim aktivnostima i bodovima. U nju sami upišete opise, datume i
          poveznice na dokaze (žuto označena mjesta), a zatim je po želji spremite kao PDF.
        </p>
        <button
          type="button"
          onClick={() => window.print()}
          data-testid="print-btn"
          className="w-full inline-flex items-center justify-center gap-2 rounded-lg bg-white border border-slate-300 px-4 py-2.5 text-sm font-medium text-slate-700 hover:bg-slate-50 transition-colors duration-200 focus-visible:ring-2 focus-visible:ring-blue-600 focus-visible:outline-none"
        >
          <Printer size={16} /> Ispiši / spremi kao PDF
        </button>
      </div>
    </section>
  );
};
